import cloneDeep from 'lodash/cloneDeep.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import isNull from 'lodash/isNull.js';

import { handleResponseError } from '@/Helpers.js';
import axios from '@/plugins/axios.js';
import loadingService from '@/services/LoadingService.js';
import toastService from '@/services/ToastService.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

class Quiz {
    constructor(store, mutation) {
        this.store = store;
        this.mutation = mutation;
        this.target = mutation.payload.target;
    }

    async reorder() {
        const { type } = this.mutation.payload;
        let payload;

        if (type === 'questions') {
            payload = this.store.getters['QuizBuilder/get'](this.target).questions;

            await Promise.all(
                payload.map(async item => {
                    await this.saveQuestion(item, item.display_order);
                })
            );

            return;
        }

        payload = this.store.getters[`QuizBuilder/${this.target}/question`]({
            order: this.mutation.payload.parent,
        });

        await this.saveQuestion(payload, payload.display_order);
    }

    async save({ type }) {
        if (type === 'quiz') {
            const quiz = this.store.getters['QuizBuilder/get'](this.mutation.payload.target);

            if (quiz.type && quiz.type.includes('uiz')) {
                quiz.type = null;
            }

            if (!this.validate({ type: 'quiz', item: quiz })) return;

            await this.saveQuiz(quiz);
        }

        if (type === 'question' || type === 'answer') {
            const context = this.mutation.payload[type.includes('ans') ? 'parent' : 'order'];

            const question = this.store.getters[`QuizBuilder/${this.mutation.payload.target}/question`]({
                order: context,
            });

            if (!this.validate({ type: 'question', item: question })) return;

            await this.saveQuestion(question, context);
        }

        if (type === 'reorder') {
            await this.reorder();
        }
    }

    async saveQuestion(question, context) {
        this.store.dispatch('AutoSave/setSaving', this.mutation.type);

        const quiz = this.store.getters['QuizBuilder/get'](this.mutation.payload.target);

        const type = question.id ? 'put' : 'post';
        const endpoint = question.id ? `questions/${question.id}` : 'questions';
        const api = new QueryBuilder(endpoint);

        api.raw({ show_correct: true });

        if (this.mutation.payload?.query) {
            api.raw({ duplication: false });
        }

        const payload = cloneDeep(question);

        payload.quiz_type = quiz.type;

        payload.display_order = Number(payload.display_order);

        payload.answers.forEach(answer => (answer.display_order = Number(answer.display_order)));

        const { data } = await axios[type](api.toUrl(), payload)
            .then(resp => {
                this.store.dispatch('AutoSave/setSaved', this.mutation.type);

                return resp;
            })
            .catch(error => {
                this.store.dispatch('AutoSave/setError', {
                    indicator: this.mutation.type,
                    error,
                });
            });

        const { answers } = data.data;

        this.store.commit(`QuizBuilder/${this.mutation.payload.target}/SET_QUESTION_FIELD`, {
            field: 'id',
            order: context,
            value: data.data.id,
        });

        if (!isEmpty(answers)) {
            answers.forEach(answer => {
                this.store.commit(`QuizBuilder/${this.mutation.payload.target}/SET_ANSWER_FIELD`, {
                    field: 'id',
                    parent: context,
                    order: answer.display_order,
                    value: answer.id,
                });

                this.store.commit(`QuizBuilder/${this.mutation.payload.target}/SET_ANSWER_FIELD`, {
                    field: 'question_id',
                    parent: context,
                    order: answer.display_order,
                    value: data.data.id,
                });
            });
        }

        const meta = get(data, 'meta.current_version');

        if (!this.mutation.payload?.query) {
            await this.handleVersionChange({ meta, quiz, question });

            return;
        }

        await this.updateQuestionCount(quiz);
    }

    async saveQuiz(quiz) {
        try {
            this.store.dispatch('AutoSave/setSaving', this.mutation.type);

            const type = quiz.id ? 'put' : 'post';
            let api = quiz.id ? `/api/quizzes/${quiz.id}` : '/api/quizzes';

            if (this.mutation.payload?.query) {
                api += `?${this.mutation.payload.query}`;
            }

            quiz.question_count = quiz.questions.length;

            if (!quiz.quiz_version_id && quiz.id) {
                quiz.quiz_version_id = quiz.id;
            }

            const { parent } = quiz;

            delete quiz.parent;

            const payload = cloneDeep(quiz);

            quiz.parent = parent;

            const { data } = await axios[type](api, payload, {
                errorHandle: false,
            })
                .then(resp => {
                    this.store.dispatch('AutoSave/setSaved', this.mutation.type);

                    return resp;
                })
                .catch(error => {
                    this.store.dispatch('AutoSave/setError', {
                        indicator: this.mutation.type,
                        error,
                    });
                });

            this.store.commit(`QuizBuilder/${quiz.uuid}/SET_QUIZ_FIELD`, {
                field: 'id',
                value: data.data.id,
            });

            const meta = get(data, 'meta.current_version');

            if (!this.mutation.payload?.query) {
                await this.handleVersionChange({ meta, quiz });
            }
        } catch (error) {
            handleResponseError(error);
        }
    }

    async handleVersionChange({ meta, quiz, question = null }) {
        const newQuiz = !quiz.quiz_version_id;

        if (isEmpty(meta)) return;

        if (Number(meta.versionable_id) !== Number(quiz.quiz_version_id) && !newQuiz) {
            await this.updateVersion({ quiz, meta, question });
        }

        if (newQuiz) {
            this.store.commit(`QuizBuilder/${quiz.uuid}/SET_QUIZ_FIELD`, {
                field: 'quiz_version_id',
                value: meta.versionable_id,
            });

            this.store.commit(`QuizBuilder/${quiz.uuid}/SET_QUIZ_FIELD`, {
                field: 'quiz_version_number',
                value: meta.version_number,
            });

            this.store.commit(`QuizBuilder/${quiz.uuid}/SET_QUIZ_FIELD`, {
                field: 'original_version_id',
                value: meta.versionable_id,
            });
        }
    }

    async updateVersion({ quiz, meta, question = null }) {
        const loading = loadingService.open({});

        try {
            const open = toastService.open({
                message: 'New version detected, updating...',
                indefinite: true,
            });

            const parent = this.store.getters[`QuizBuilder/${quiz.uuid}/get`]('parent');

            await this.store.dispatch('QuizBuilder/closeEditor');

            await this.store.dispatch('QuizBuilder/editQuiz', {
                quiz_id: meta.versionable_id,
                parent,
            });

            if (question) {
                this.store.commit(`QuizBuilder/${meta.versionable_id}/SET_QUESTION_FIELD`, {
                    field: 'expanded',
                    order: question.display_order,
                    value: true,
                });
            }

            open.close();

            toastService.open({ message: 'Update complete!' });

            loading.close();
        } catch (_e) {
            toastService.open({
                queue: false,
                type: 'is-danger',
                message: 'Error during update, please contact support.',
                duration: 3500,
            });

            loading.close();
        }
    }

    async updateQuestionCount(quiz) {
        if (quiz.question_count !== quiz.questions.length) {
            await this.saveQuiz(quiz);
        }
    }

    validate({ type, item }) {
        if (type === 'question') return this.validateQuestion(item);

        if (type === 'quiz') return this.validateQuiz(item);
    }

    validateQuestion(question) {
        if (!question.body) return false;

        if (
            question.type === 'multiple_choice_multiple_answer' &&
            !question.partial_credit &&
            isNull(question.points) &&
            !this.isSurvey()
        )
            return false;

        if (
            question.type === 'multiple_choice_multiple_answer' &&
            (question.points % 0.25 !== 0 || question.points < 0)
        )
            return false;

        return this.validateAnswer(question.answers);
    }

    validateAnswer(answers) {
        if (answers.some(item => item.type === 'free_form')) return true;

        if (answers.length < 1) return false;

        if (answers.some(item => !item.body)) return false;

        if (this.isSurvey()) return true;

        if (answers.some(item => !item.value && typeof item.value !== 'number')) return false;

        // TODO: Replace with potential institution setting for valid step

        if (answers.some(item => item.value % 0.25 !== 0)) return false;

        return answers.some(item => item.is_correct === true);
    }

    validateQuiz(quiz) {
        return !!quiz.title;
    }

    isSurvey() {
        const id = this.mutation.payload.target;
        const quizType = this.store.getters[`QuizBuilder/${id}/get`]('type');

        return quizType === 'survey';
    }
}

export default Quiz;
